'use client'

import * as React from 'react'
import { MoreHorizontalIcon } from 'lucide-react'
import { MorphIcon } from 'morphicons/react'

import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from '@/lib/icons/lucide-icon-data'
import { usePrefersReducedMotion } from '@/lib/use-prefers-reduced-motion'
import { cn } from '@/lib/utils'
import { buttonVariants, type Button } from '@/components/ui/button'

function Pagination({ className, ...props }: React.ComponentProps<'nav'>) {
  return (
    <nav
      role="navigation"
      aria-label="pagination"
      data-slot="pagination"
      className={cn('mx-auto flex w-full justify-center', className)}
      {...props}
    />
  )
}

function PaginationContent({ className, ...props }: React.ComponentProps<'ul'>) {
  return (
    <ul
      data-slot="pagination-content"
      className={cn('flex flex-row items-center gap-2', className)}
      {...props}
    />
  )
}

function PaginationItem({ ...props }: React.ComponentProps<'li'>) {
  return <li data-slot="pagination-item" {...props} />
}

type PaginationLinkProps = {
  isActive?: boolean
} & Pick<React.ComponentProps<typeof Button>, 'size'> &
  React.ComponentProps<'a'>

function PaginationLink({ className, isActive, size = 'icon-xs', ...props }: PaginationLinkProps) {
  return (
    <a
      aria-current={isActive ? 'page' : undefined}
      data-slot="pagination-link"
      data-active={isActive}
      className={cn(
        buttonVariants({
          variant: isActive ? 'default' : 'ghost',
          size,
        }),
        'text-sm',
        !isActive && 'hover:bg-interaction-neutral hover:text-foreground',
        className,
      )}
      {...props}
    />
  )
}

function PaginationPrevious({ className, ...props }: React.ComponentProps<typeof PaginationLink>) {
  return <PaginationDirectionalLink direction="previous" className={className} {...props} />
}

function PaginationNext({ className, ...props }: React.ComponentProps<typeof PaginationLink>) {
  return <PaginationDirectionalLink direction="next" className={className} {...props} />
}

function PaginationDirectionalLink({
  direction,
  className,
  onMouseEnter,
  onMouseLeave,
  onFocus,
  onBlur,
  ...props
}: React.ComponentProps<typeof PaginationLink> & { direction: 'previous' | 'next' }) {
  const [active, setActive] = React.useState(false)
  const prefersReducedMotion = usePrefersReducedMotion()
  const disabled = props['aria-disabled'] === true || props['aria-disabled'] === 'true'
  const isPrevious = direction === 'previous'
  const icon =
    prefersReducedMotion || !active || disabled
      ? isPrevious
        ? ChevronLeft
        : ChevronRight
      : isPrevious
        ? ChevronsLeft
        : ChevronsRight

  const morphIcon = (
    <MorphIcon
      icon={icon}
      spring="snappy"
      size={16}
      reducedMotion={prefersReducedMotion ? 'user' : 'never'}
      aria-hidden
      className="shrink-0"
    />
  )

  return (
    <PaginationLink
      aria-label={`Go to ${direction} page`}
      size="default"
      className={cn('gap-1 px-2.5', isPrevious ? 'sm:pl-2.5' : 'sm:pr-2.5', className)}
      onMouseEnter={(event) => {
        setActive(true)
        onMouseEnter?.(event)
      }}
      onMouseLeave={(event) => {
        setActive(false)
        onMouseLeave?.(event)
      }}
      onFocus={(event) => {
        setActive(true)
        onFocus?.(event)
      }}
      onBlur={(event) => {
        setActive(false)
        onBlur?.(event)
      }}
      {...props}
    >
      {isPrevious && morphIcon}
      <span className="hidden text-sm font-medium lowercase first-letter:uppercase sm:block">
        {isPrevious ? 'Previous' : 'Next'}
      </span>
      {!isPrevious && morphIcon}
    </PaginationLink>
  )
}

function PaginationEllipsis({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      aria-hidden
      data-slot="pagination-ellipsis"
      className={cn('flex size-9 items-center justify-center', className)}
      {...props}
    >
      <MoreHorizontalIcon className="size-4" />
      <span className="sr-only">More pages</span>
    </span>
  )
}

export {
  Pagination,
  PaginationContent,
  PaginationLink,
  PaginationItem,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
}
